import { useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import TurnstileWidget from '@/components/marketing/TurnstileWidget'
import { useFormState } from '@/hooks/useFormState'
import { useValidation } from '@/hooks/useValidation'
import { useOtp } from '@/hooks/useOtp'
import { usePayment } from '@/hooks/usePayment'
import { useWebMcp } from '@/hooks/useWebMcp'
import { track } from '@/lib/analytics'
import type { SharedMarketingProps } from '@/types/marketing'
import StepPersonal from '@/components/apply/StepPersonal'
import StepAddress from '@/components/apply/StepAddress'
import StepRole from '@/components/apply/StepRole'
import StepDemos from '@/components/apply/StepDemos'
import StepSocial from '@/components/apply/StepSocial'
import StepPlan from '@/components/apply/StepPlan'
import OtpOverlay from '@/components/apply/OtpOverlay'
import PaymentModal from '@/components/apply/PaymentModal'
import type { ApplyPageProps, Country } from '@/types/apply'
import { TOTAL_STEPS, STEP_LABELS } from '@/types/apply'

function ProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => {
        const step = i + 1
        const isActive = step === current
        const isCompleted = step < current
        return (
          <div key={step} className="flex-1">
            <div
              className={`h-1 w-full rounded-full transition-colors duration-300 ${
                isCompleted ? 'bg-green-500' : isActive ? 'bg-[#ff6a1a]' : 'bg-white/10'
              }`}
            />
          </div>
        )
      })}
    </div>
  )
}

export default function Apply(props: ApplyPageProps) {
  const { formData, currentStep, updateField, updateDemoLinks, mergeFormData, nextStep, prevStep, goToStep } =
    useFormState(props.default_currency)
  const { errors, validateStep, clearError, clearAllErrors } = useValidation()
  const otp = useOtp()
  const payment = usePayment(props.gateways, props.razorpay_key_id)

  // Expose WebMCP tools to AI agents in supporting browsers (feature-detected,
  // no-op otherwise): read pricing/plans, check country eligibility, and
  // prefill the form. Submission stays gated behind OTP + Turnstile + payment.
  useWebMcp({
    plans: props.plans,
    pricing: props.pricing,
    countries: props.countries,
    supportedCurrencies: props.supported_currencies,
    defaultCurrency: props.default_currency,
    mergeFormData,
    goToStep,
    onPrefill: () => track('apply_prefill_via_agent', { data: { source: 'webmcp' } }),
  })
  const formRef = useRef<HTMLDivElement>(null)
  const showOtp = useRef(false)
  const showPaymentModal = useRef(false)
  const applyStartedRef = useRef(false)
  const prevStepRef = useRef(currentStep)

  // Pre-fill geo defaults on fresh form
  useEffect(() => {
    if (!props.geo_country_code) return
    const country = props.countries.find(
      (c: Country) => c.code === props.geo_country_code
    )
    if (country && !formData.address.country) {
      updateField('address', 'country', country.name)
    }
    if (country && !formData.personal_info.phone_iso_code) {
      updateField('personal_info', 'phone_iso_code', country.code)
      updateField('personal_info', 'phone_dial_code', country.dial_code)
    }
  }, [props.geo_country_code])

  // Pre-fill invitation code from URL
  useEffect(() => {
    if (props.invitation_code && props.invitation_code !== 'NA' && !formData.subscription.invitation_code) {
      updateField('subscription', 'invitation_code', props.invitation_code)
    }
  }, [props.invitation_code])

  // Scroll to form on step change
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [currentStep])

  // Fire apply_step_completed once per forward step advance (steps 1-5; the
  // final step's completion is the application_submitted conversion instead).
  useEffect(() => {
    if (currentStep > prevStepRef.current) {
      const completed = prevStepRef.current
      track('apply_step_completed', {
        data: { step: completed, step_name: STEP_LABELS[completed - 1] },
      })
    }
    prevStepRef.current = currentStep
  }, [currentStep])

  const turnstileTokenRef = useRef<string | null>(null)
  const [turnstileVerified, setTurnstileVerified] = useState(false)
  const { props: sharedProps } = usePage<SharedMarketingProps>()
  const turnstileSiteKey = sharedProps.layout?.turnstile?.site_key || null

  function handleNext() {
    clearAllErrors()
    if (!validateStep(currentStep, formData)) return

    if (currentStep === 1 && otp.otpStatus !== 'verified') {
      showOtp.current = true
      otp.sendOtp(formData.personal_info.email.trim().toLowerCase())
      forceUpdate()
      return
    }

    if (currentStep === TOTAL_STEPS) {
      if (turnstileSiteKey && !turnstileVerified) return
      showPaymentModal.current = true
      forceUpdate()
      return
    }

    nextStep()
  }

  function handlePrev() {
    clearAllErrors()
    prevStep()
  }

  // Stable keyboard ref
  const handleNextRef = useRef(handleNext)
  handleNextRef.current = handleNext

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement) return
      if (e.target instanceof HTMLInputElement && e.target.type !== 'radio') return
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleNextRef.current()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const [, setTick] = useState(0)
  function forceUpdate() { setTick(t => t + 1) }

  function handleOtpClose() {
    showOtp.current = false
    forceUpdate()
  }

  function handleOtpVerified() {
    showOtp.current = false
    forceUpdate()
    nextStep()
  }

  function handlePaymentClose() {
    showPaymentModal.current = false
    forceUpdate()
  }

  function handlePaymentSubmit(gateway: string) {
    showPaymentModal.current = false
    forceUpdate()
    payment.submitApplication(formData, gateway, turnstileTokenRef.current ?? undefined)
  }

  // Fire the payment conversion exactly once when the inline flow completes.
  // The submission conversion (Lead) fires in usePayment at POST success, so
  // it also covers Stripe redirects and stalled payments — not here.
  useEffect(() => {
    if (payment.paymentStatus === 'success' && payment.applicationToken) {
      const sub = formData.subscription
      // First month's subscription cost (always the monthly price, even for a
      // yearly signup) — the acquisition value for the conversion.
      const firstMonth = props.pricing[sub.plan]?.['monthly']?.[sub.currency]?.discounted_price ?? 0
      const dims = {
        profile_type: formData.role_info.role || undefined,
        plan_slug: sub.plan,
        country: formData.address.country || undefined,
      }
      // This inline success screen is reached by the Razorpay (and fake) paths —
      // the Stripe path lands on PaymentComplete instead. Fire the Purchase here
      // too so card/UPI-via-Razorpay payers also report the conversion to Google
      // Ads + Meta, matching the Stripe page.
      track('payment_success', {
        metaEvent: 'Purchase',
        conversion: 'payment_success',
        data: { application_token: payment.applicationToken, value: firstMonth, currency: sub.currency, ...dims },
      })
    }
  }, [payment.paymentStatus, payment.applicationToken])

  // Payment success: show inline success. Same v2 atmosphere (solid
  // black + glows, no bg image) as the application form view.
  if (payment.paymentStatus === 'success' && payment.applicationToken) {
    return (
      <AtmosphericHero minHeight="60vh">
        <ScrollReveal direction="up">
          <div className="mx-auto max-w-lg text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
              <svg className="h-10 w-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="marketing-display text-3xl">Application Submitted!</h1>
            <p className="marketing-lead mt-4">
              Your application has been received. We'll review it and get back to you soon.
            </p>
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] px-6 py-4">
              <p className="text-sm text-white/50">Your application token</p>
              <p className="mt-1 font-mono text-lg text-[#ff6a1a]">{payment.applicationToken}</p>
            </div>
            <div className="mt-8 flex justify-center gap-4">
              <a href="/" className="marketing-button-secondary">Back to Home</a>
              <a href={`/status/${payment.applicationToken}`} className="marketing-button-primary">Check Status</a>
            </div>
          </div>
        </ScrollReveal>
      </AtmosphericHero>
    )
  }

  // Fire apply_started once, on the first real field interaction. Wrappers are
  // passed to the step components only — the programmatic geo/invitation
  // pre-fill effects above use the raw updateField so they don't count.
  function markApplyStarted() {
    if (applyStartedRef.current) return
    applyStartedRef.current = true
    track('apply_started', {
      metaEvent: 'InitiateCheckout',
      data: { step: currentStep, profile_type: formData.role_info.role || undefined },
    })
  }

  const trackedUpdateField: typeof updateField = (step, field, value) => {
    markApplyStarted()
    updateField(step, field, value)
  }

  const trackedUpdateDemoLinks: typeof updateDemoLinks = (...args) => {
    markApplyStarted()
    return updateDemoLinks(...args)
  }

  const stepProps = { formData, updateField: trackedUpdateField, errors, clearError }

  return (
    <>
      {/* Hero — clear, compact: eyebrow + headline + SEO copy. Form
          lives in its own section below. */}
      <AtmosphericHero minHeight="56vh" containerClassName="hero-content">
        <div className="hero-text mx-auto text-center" style={{ maxWidth: '48rem' }}>
          <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Join wavbee</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}>
            Apply for music distribution
          </h1>
          <p className="hero-body mx-auto">
            Apply to wavbee — the music distribution platform built for independent artists
            and labels worldwide. Get your releases on Spotify, Apple Music, YouTube Music,
            TikTok, JioSaavn, and 100+ other platforms. Keep 100% of your masters, earn up
            to 100% royalties, and get real human support from a team that responds within
            12 hours. Most applications are reviewed within 1–3 business days.
          </p>
        </div>
      </AtmosphericHero>

      {/* Form — separate marketing-section below the hero (like the
          Pricing page splits its plan grid into its own section). Dark
          surface, full container, max-w-4xl form card centred inside. */}
      <section className="marketing-section">
        <div ref={formRef} className="container mx-auto px-6" style={{ scrollMarginTop: '6rem' }}>
          <ScrollReveal direction="up">
            <div className="mx-auto max-w-4xl">
              <div className="apply-form-card">
                {/* Progress bar inside card */}
                <div className="mb-6">
                  <ProgressBar current={currentStep} total={TOTAL_STEPS} />
                </div>

                {/* Step header */}
                <div className="mb-5 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">
                    {STEP_LABELS[currentStep - 1]}
                  </h2>
                  <span className="text-xs font-medium text-white/35">
                    {currentStep} / {TOTAL_STEPS}
                  </span>
                </div>

                {/* Step content */}
                <div>
                  {currentStep === 1 && <StepPersonal {...stepProps} countries={props.countries} />}
                  {currentStep === 2 && <StepAddress {...stepProps} countries={props.countries} />}
                  {currentStep === 3 && <StepRole {...stepProps} />}
                  {currentStep === 4 && <StepDemos formData={formData} updateDemoLinks={trackedUpdateDemoLinks} errors={errors} clearError={clearError} />}
                  {currentStep === 5 && <StepSocial {...stepProps} />}
                  {currentStep === 6 && (
                    <>
                      <StepPlan
                        {...stepProps}
                        pricing={props.pricing}
                        defaultCurrency={props.default_currency}
                        supportedCurrencies={props.supported_currencies}
                        tokenCharges={props.token_charges}
                        plans={props.plans}
                      />
                      {turnstileSiteKey && (
                        <div className="mt-6">
                          <p className="mb-2 text-xs text-white/40">Please confirm you&apos;re human:</p>
                          <TurnstileWidget
                            siteKey={turnstileSiteKey}
                            action="apply"
                            onVerify={(token) => {
                              turnstileTokenRef.current = token
                              setTurnstileVerified(true)
                            }}
                            onExpire={() => {
                              turnstileTokenRef.current = null
                              setTurnstileVerified(false)
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Navigation buttons */}
                <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/[0.06] pt-6">
                  {currentStep > 1 ? (
                    <button type="button" onClick={handlePrev} className="marketing-button-secondary">
                      Previous
                    </button>
                  ) : (
                    <div />
                  )}

                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={
                      payment.paymentStatus === 'submitting' ||
                      payment.paymentStatus === 'redirecting' ||
                      (currentStep === TOTAL_STEPS && Boolean(turnstileSiteKey) && !turnstileVerified) ||
                      (currentStep === TOTAL_STEPS && !(
                        formData.agreements.terms_of_service &&
                        formData.agreements.ai_music_policy &&
                        formData.agreements.reapply_policy
                      ))
                    }
                    className="marketing-button-primary"
                  >
                    {currentStep === TOTAL_STEPS ? 'Submit Application' : 'Continue'}
                  </button>
                </div>

                {payment.paymentError && (
                  <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {payment.paymentError}
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {showOtp.current && (otp.otpStatus !== 'verified' || otp.existingApp) && (
        <OtpOverlay
          email={formData.personal_info.email}
          otp={otp}
          onClose={handleOtpClose}
          onVerified={handleOtpVerified}
        />
      )}

      {showPaymentModal.current && (
        <PaymentModal
          gateways={props.gateways}
          onClose={handlePaymentClose}
          onSubmit={handlePaymentSubmit}
          loading={payment.paymentStatus === 'submitting'}
        />
      )}
    </>
  )
}
